import { Button, DynamicContainer, FormSection } from '@hai/ui-react';
import { ButtonStateType, ITypeaheadListItemsProps } from '@hai/ui-react/dist/types';
import { FormikProps } from 'formik';
import { includes, isNil, range } from 'ramda';
import React, { SyntheticEvent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FormSelect, SelectOptions } from 'src/js/component/base/form/form-select';
import { FormTextfield } from 'src/js/component/base/form/form-textfield';
import { FormTypeahead } from 'src/js/component/base/form/form-typeahead';
import systemInfo, { systemInfoCapabilities } from 'src/js/data/systemInfo';
import { useStores } from 'src/js/hook/use-stores';
import { FormChangeEvent } from 'src/js/util/global-type';
import { submitAndValidate } from 'src/js/validator/validator';

import { EssenceState, EssenceType, EssenceViewModel } from '../essence/essence-models';
import essenceModels from '../essence/essence-store';
import { MkRange, VIDIN_CONNECTOR } from '../video-encoder/video-encoder-models';
import videoEncoders from '../video-encoder/video-encoder-store';
import {
  AudioEncoderMode,
  AudioEncoderViewModel,
  AudioInterfaceEnum,
  getAudioInterfaceLabels,
  audioLanguages,
  encoderMode,
  AudioAlgorithmEnum,
} from './audio-encoder-models';
import { ApiResponse } from 'apisauce';
import { removeItemFromArray } from 'src/js/util/global-util';

interface Props {
  applyButtonState: ButtonStateType;
  formikProps: FormikProps<AudioEncoderViewModel>;
  getBitRateRange: () => MkRange;
  onCancel: VoidFunction;
  setBitRateRange: (newValue: MkRange) => void;
}

export const AudioEncoderEditForm: React.FunctionComponent<Props> = ({
  applyButtonState,
  getBitRateRange,
  formikProps,
  onCancel,
  setBitRateRange,
}) => {
  const { t } = useTranslation();
  const { sessionStore } = useStores();

  const values = formikProps.values;
  const isCurrentValueAnalog = values.interface === 0;

  const [inputInterfaceOptions, setInputInterfaceOptions] = useState<SelectOptions>();
  const [channelLayoutOptions, setChannelLayoutOptions] = useState<SelectOptions>();
  const [interfaceTweaked, setInterfaceTweaked] = useState<AudioInterfaceEnum | false>(false); // NKE-3056

  const isAudencIdWithinEssence = (id: number, essence: EssenceViewModel) => {
    const inputChannel = essence.inputChannel;
    const numOfChannels = essence.numOfChannels;
    const idBase = 1 + inputChannel * 8;
    const amountOfPairs = numOfChannels / 2;
    const idMax = idBase + Math.ceil(amountOfPairs) - 1;
    return id >= idBase && id <= idMax;
  };

  const mixAndMatchEssencesInAudioDropdown = (options: SelectOptions) => {
    if (!systemInfoCapabilities.is2110()) {
      return options;
    }

    for (let i = 0; i < options.length; i++) {
      let coveredByEssence = false;
      const optionId = Number(options[i].value);
      essenceModels
        .filter((e) => e.type === EssenceType.audio && e.state === EssenceState.RUNNING)
        .forEach((model) => {
          const withinStartedEssence = isAudencIdWithinEssence(optionId, model);
          if (withinStartedEssence) {
            if (model.name?.length) {
              options[i].label = options[i].label + ' "' + model.name + '"';
            } else {
              // no change to text
            }
            coveredByEssence = true;
          }
        });

      if (coveredByEssence === false) {
        options[i].disabled = true;
      }
    }
    return options;
  };

  // Function getInterfaceOptions()
  // Calls setInputInterfaceOptions() and setChannelLayoutOptions() to populate dropdowns options
  const getInterfaceOptions = (layout: AudioEncoderMode) => {
    let interfacesRange =
      systemInfoCapabilities.is2110() || !systemInfoCapabilities.isRugged() ? range(1, 9) : range(0, 9); // Analog or not switch

    if (videoEncoders[0]?.inputInterface === VIDIN_CONNECTOR.HDMI) {
      interfacesRange = [AudioInterfaceEnum.HDMI_CH12];
    }
    if (systemInfo.videoInputs >= 2) {
      interfacesRange = interfacesRange.concat(range(9, 17));
    }
    if (systemInfo.videoInputs >= 3) {
      interfacesRange = interfacesRange.concat(range(17, 25));
    }
    if (systemInfo.videoInputs >= 4) {
      interfacesRange = interfacesRange.concat(range(25, 33));
    }

    //Input Interface Options ex.SDI-1
    let inputInterfaceOptions: SelectOptions = [];
    const fullMap = interfacesRange
      .map((item) => {
        return getAudioInterfaceLabels(item, layout);
      })
      .filter((m) => m !== undefined);
    const inputInterfaceList = fullMap.map((item) => {
      return item.inputInterface;
    });

    // remove duplicates SDI-1, SDI-1, SDI-2, SDI-2, ... => SDI-1, SDI-2, ...
    // TODO using [...new Set(list)] would be faster if our JS version allows
    const uniqueInputInterfaceList = inputInterfaceList.filter(
      (item, index) => inputInterfaceList.indexOf(item) === index,
    );

    uniqueInputInterfaceList.forEach((value) =>
      inputInterfaceOptions.push({
        value: value,
        label: value,
      }),
    );
    inputInterfaceOptions = mixAndMatchEssencesInAudioDropdown(inputInterfaceOptions);
    const selectedInput = values.inputInterface;

    // Channel Layout Options ex. Ch. 1-2
    const channelLayoutOptions: SelectOptions = []; // FIXME rename, it clashes with state above
    let valueMatchedInOptions = false;
    fullMap.forEach((value) => {
      //if (!value.interfaceStr.includes(selectedInput)) { // does 'SDI-1 Ch. 1-2' includes 'SDI-1'
      if (!value.interfaceStr.startsWith(selectedInput + ' ')) {
        // does 'SDI-1 Ch. 1-2' starts with 'SDI-1 '
        return;
      }
      channelLayoutOptions.push({
        value: value.value,
        label: value.inputChannel,
      });
      if (value.value === values.interface) {
        // valid value
        valueMatchedInOptions = true;
      }
    });

    if (!valueMatchedInOptions) {
      if (channelLayoutOptions?.length > 0) {
        setInterfaceTweaked(values.interface);
        formikProps.setFieldTouched('interface');
        formikProps.setFieldValue('interface', channelLayoutOptions[0].value);
      }
    } else if (
      interfaceTweaked &&
      interfaceTweaked !== values.interface &&
      channelLayoutOptions?.find((o) => o.value === interfaceTweaked)
    ) {
      // reset back tweak
      formikProps.setFieldValue('interface', interfaceTweaked);
      setInterfaceTweaked(false);
    }

    setInputInterfaceOptions(inputInterfaceOptions); // should be max 4 input
    setChannelLayoutOptions(channelLayoutOptions); // layout ex.:  1-2, 2-3, ... 7-8

    const currentLayout = values.interface;
    const isInOptions = channelLayoutOptions.find((o) => o.value === currentLayout);
    const currentInterfaceOpt = fullMap.find((item) => item.value === values.interface);
    if (isInOptions === undefined) {
      // lets find labelL currentInterfaceOpt?.inputChannel
      const newInterfaceEnumValue = channelLayoutOptions.find(
        (option) => option.label === currentInterfaceOpt?.inputChannel,
      );
      if (newInterfaceEnumValue && Number(newInterfaceEnumValue.value) > 0) {
        formikProps.setFieldTouched('interface');
        formikProps.setFieldValue('interface', newInterfaceEnumValue.value);
      } else if (values.inputInterface === 'Analog') {
        // special case 'Analog', disable "interface" dropdown at grayed out label "Ch. 1-2" but with value Analog
        formikProps.setFieldTouched('interface');
        formikProps.setFieldValue('interface', AudioInterfaceEnum.ANALOG);
      }
    }
  };

  /* UNUSED as of 1.8
  const getNearestBitrate = (bitrates: number[], bitrate: number, changedToMono: boolean) => {
    if (bitrates.includes(bitrate)) {
      return bitrate;
    }

    for (let i = 0; i < bitrates.length; i++) {
      if (bitrates[i] > bitrate) {
        return changedToMono ? bitrates[Math.max(0, i - 1)] : bitrates[i];
      }
    }

    return bitrates[bitrates.length - 1];
  };*/

  const [bitrateOptions, setBitrateOptions] = useState<SelectOptions>();
  const getBitrateOptions = (mode: AudioEncoderMode, sampleRate: number, _changedToMono = false) => {
    let bitrates: number[] = [];

    const generateOptions = (values: number[]): SelectOptions =>
      values.map((v) => ({
        value: v,
        label: `${v} kbps`,
      }));

    //Tables from NKE-2935
    if (
      sampleRate === 16 &&
      [AudioEncoderMode.RIGHT, AudioEncoderMode.LEFT, AudioEncoderMode.STEREO].some((e) => e === mode)
    ) {
      // 16 kHz special cases(16 kHz is for analog only)
      switch (mode) {
        case AudioEncoderMode.STEREO:
          bitrates = [5, 6, 7, 8, 9, 10, 12, 14, 16, 32, 48, 64, 80, 96, 128, 192];
          break;
        case AudioEncoderMode.LEFT:
        case AudioEncoderMode.RIGHT:
        default:
          bitrates = [4, 5, 6, 7, 8, 9, 10, 12, 16, 24, 32, 40, 48, 56, 96];
      }
    } else {
      // 16kHZ Surround Sound or 48 kHz
      switch (mode) {
        case AudioEncoderMode.STEREO:
          bitrates = [14, 16, 32, 48, 64, 80, 96, 128, 192, 256, 320, 384, 448, 512, 576];
          break;
        case AudioEncoderMode.SURROUND_THREE:
          bitrates = [16, 32, 56, 80, 104, 128, 152, 192, 288, 384, 480, 576, 672, 768, 864];
          break;
        case AudioEncoderMode.SURROUND_FOUR:
          bitrates = [18, 48, 80, 112, 144, 176, 208, 256, 384, 512, 640, 768, 896, 1024, 1152];
          break;
        case AudioEncoderMode.SURROUND_FIVE:
          bitrates = [20, 48, 88, 128, 168, 208, 248, 320, 480, 640, 800, 960, 1120, 1280, 1440];
          break;
        case AudioEncoderMode.SURROUND_SIX: // 5.1
          bitrates = [21, 48, 88, 128, 168, 208, 248, 320, 480, 640, 800, 960, 1120, 1280, 1440];
          break;
        // No bitrate selection for 7.0 because it's only for passthrough and we dont show bitrates for passthroug
        case AudioEncoderMode.SURROUND_EIGHT:
          bitrates = [25, 64, 120, 176, 232, 288, 344, 448, 672, 896, 1120, 1344, 1568, 1792, 2016];
          break;
        case AudioEncoderMode.LEFT:
        case AudioEncoderMode.RIGHT:
        default: {
          bitrates = [12, 16, 24, 32, 40, 48, 56, 64, 96, 128, 160, 192, 224, 256, 288];
        }
      }
    }

    const bitRateRange = { max: bitrates[bitrates.length - 1], min: bitrates[0] } as MkRange;
    setBitRateRange(bitRateRange);

    setBitrateOptions(generateOptions(bitrates));

    const outOfRange = values.bitRate < bitrates[0] || values.bitRate > bitrates[bitrates.length - 1];
    if (formikProps && !includes(values.bitRate, bitrates) && outOfRange) {
      formikProps.setFieldTouched('bitRate'); // to trigger validation red error text
    }

    /* UNUSED as of 1.8, we dont select nearest anymore. Instead show validation error.
    if (formikProps && !includes(values.bitRate, bitrates) && outOfRange && changedToMono) {
      // dont do it all the time because we accept manual values beside offered selection. Note if we want to use a value from the list if Mono just got selected add || changedToMono here
      // automatically select nearest
      const bitrate = getNearestBitrate(bitrates, values.bitRate, changedToMono);
      console.log('wiht mono changed, changing bitrate to', bitrate)
      formikProps.setFieldValue('bitRate', bitrate);
    }*/
  };

  const [sampleRateOptions, setSampleRateOptions] = useState<SelectOptions>();
  const getSampleRateOptions = (isAnalog: boolean) => {
    setSampleRateOptions(
      isAnalog
        ? [
            { value: 48, label: '48 kHz' },
            { value: 16, label: '16 kHz' },
          ]
        : [{ value: 48, label: '48 kHz' }],
    );
  };

  const [inputLevelOptions, setInputLevelOptions] = useState<SelectOptions>();
  const getInputLevelOptions = () => {
    if (systemInfoCapabilities.isRugged()) {
      const levels = systemInfo.hasEth1 ? [0, 1, 2, 3, 4, 5, 6] : [0, -1, -2, -3, -4, -5, -6];
      setInputLevelOptions(
        levels.map((v) => ({
          value: v,
          label: `${v} dBu`,
        })),
      );
    }
  };

  const [langOptions, setLangOptions] = useState<SelectOptions>();
  const getLangOptions = () => {
    const languages = Object.keys(audioLanguages).map((lang) => ({
      value: lang,
      label: `${audioLanguages[lang]} (${lang})`,
    }));
    setLangOptions(languages);
  };

  const modeOptions = [
    {
      value: AudioEncoderMode.STEREO,
      label: encoderMode(AudioEncoderMode.STEREO, t),
    },
    {
      value: AudioEncoderMode.LEFT,
      label: encoderMode(AudioEncoderMode.LEFT, t),
    },
    {
      value: AudioEncoderMode.RIGHT,
      label: encoderMode(AudioEncoderMode.RIGHT, t),
    },
    {
      value: AudioEncoderMode.SURROUND_THREE,
      label: encoderMode(AudioEncoderMode.SURROUND_THREE, t),
    },
    {
      value: AudioEncoderMode.SURROUND_FOUR,
      label: encoderMode(AudioEncoderMode.SURROUND_FOUR, t),
    },
    {
      value: AudioEncoderMode.SURROUND_FIVE,
      label: encoderMode(AudioEncoderMode.SURROUND_FIVE, t),
    },
    {
      value: AudioEncoderMode.SURROUND_SIX,
      label: encoderMode(AudioEncoderMode.SURROUND_SIX, t),
    },
    {
      value: AudioEncoderMode.SURROUND_SEVEN,
      label: encoderMode(AudioEncoderMode.SURROUND_SEVEN, t),
    },
    {
      value: AudioEncoderMode.SURROUND_EIGHT,
      label: encoderMode(AudioEncoderMode.SURROUND_EIGHT, t),
    },
  ];

  if (isCurrentValueAnalog) {
    modeOptions.splice(3); // only offer Mono(s) and Stereo for Analog input
  }

  if (formikProps.values.algorithm !== AudioAlgorithmEnum.RAW) {
    removeItemFromArray(modeOptions, (i) => i.value === AudioEncoderMode.SURROUND_SEVEN);
  }

  if (modeOptions.find((o) => o.value === values.mode) === undefined) {
    // current value is outside of range
    // dropdown showing empty selection to be fixed
    formikProps.setFieldTouched('mode');
    formikProps.setFieldValue('mode', 0); // go back to Stereo
    // TODO add a tweak reset
  }

  const algorithmOptions = [
    { value: 10, label: t('audioEncoder.MPEG-2 AAC-LC ADTS') }, // FIXME translation
    { value: 21, label: t('audioEncoder.MPEG-4 AAC-LC LOAS/LATM') },
    { value: 1, label: t('audioEncoder.16-bit Pass-through') },
  ];

  // TODO this cause too much render, can be improved to be inline
  useEffect(() => {
    getInterfaceOptions(values.mode);
    getBitrateOptions(values.mode, values.sampleRate);
    const isAnalog = values.interface === 0;
    getSampleRateOptions(isAnalog);
    getLangOptions();
    getInputLevelOptions();
  }, [formikProps.values]); // If we do [formikProps] it will fire twice per form change
  // If we do [formikProps.values] it will fire once per form change

  const [inputLevelAnalog, setInputLevelAnalog] = useState<boolean>(isCurrentValueAnalog);

  const handleChangeInputInterface = (formEvent: FormChangeEvent) => {
    const { value } = formEvent.target;
    const isAnalog = value === 'Analog';
    setInputLevelAnalog(isAnalog);
    getSampleRateOptions(isAnalog);

    if (formikProps.values.sampleRate === 16) {
      formikProps.setFieldTouched('sampleRate');
      formikProps.setFieldValue('sampleRate', 48, true);
      getBitrateOptions(formikProps.values.mode, 48);
    }
  };

  const handleChangeMode = (formEvent: FormChangeEvent) => {
    const { value } = formEvent.target;
    getBitrateOptions(parseInt(value), values.sampleRate, parseInt(value) != AudioEncoderMode.STEREO);
  };

  const handleChangeSampleRate = (formEvent: FormChangeEvent) => {
    const { value } = formEvent.target;
    getBitrateOptions(formikProps.values.mode, parseInt(value));
  };

  const setLanguageValue = (lang: string) => {
    formikProps.setFieldTouched('lang'); // touched needs to be done before because of https://github.com/jaredpalmer/formik/issues/2083
    formikProps.setFieldValue('lang', lang || '');
    // setTimeout(() => formikProps.setFieldTouched('lang'), 0); // other way to workaround https://github.com/jaredpalmer/formik/issues/2083
  };

  const handleSelectLanguage = (lang: string | undefined) => {
    setLanguageValue(lang);
  };

  const handleOnChangeLanguage = (event: any) => {
    if (event?.target?.value === '') {
      setLanguageValue(''); // clear field
    }
  };

  const onLanguageEdit = (e: SyntheticEvent) => {
    const lang = (e.target as any)?.value;
    if (lang && lang.length === 3) {
      handleSelectLanguage(lang);
    } else if (lang && lang.length > 3) {
      const matches = /\((.{3})\)/.exec(lang);
      if (matches) {
        handleSelectLanguage(matches[1]);
      } else {
        // possibly wrong, but let it go for validation
        handleSelectLanguage(lang);
      }
    } else {
      // possibly wrong, but let it go for validation
      handleSelectLanguage(lang);
    }
  };

  const handleSelectBitrate = (bitRate: string | undefined) => {
    if (bitRate === undefined) {
      bitRate = formikProps?.values.bitRate.toString();
    }
    formikProps.setFieldTouched('bitRate');
    formikProps.setFieldValue('bitRate', Number(bitRate));

    // target for deletion: const bitrateOption: SelectOptions = [{ value: bitRate, label: bitRate + ' kbps' }];
    //setBitrateOptions(bitrateOption);
  };
  let printedBitRateValue = values.bitRate.toString();
  if (
    !(formikProps.errors?.bitRate !== undefined && formikProps.errors.bitRate.length > 0) ||
    formikProps.initialValues?.bitRate === values.bitRate
  ) {
    printedBitRateValue += ' kbps';
  } else {
    // console.log('range error', formikProps.errors?.bitRate)
  }

  if (isNil(inputInterfaceOptions) || isNil(bitrateOptions) || isNil(sampleRateOptions) || isNil(langOptions)) {
    return null;
  }

  const getLanguageTxt = (lang: string) => {
    if (lang) {
      const langOption = langOptions.find((v) => v.value === lang);
      return langOption?.label ?? lang;
    }
    return '';
  };

  return (
    <>
      <DynamicContainer minColumns={2} maxColumns={2} className="mk-mt-panel-name-align">
        <FormTextfield
          name="name"
          mask
          placeholder={`Audio Encoder ${values.id}`}
          title={t('general.name')}
          className="max-width"
        />
        {!sessionStore.isUser() && (
          <div className="d-flex ml-auto hai-mt-panel-apply-sticky">
            <Button className="hai-mr-6" onClick={onCancel}>
              {t('general.cancel')}
            </Button>
            <Button
              variant="primary"
              onClick={() => {
                return submitAndValidate(formikProps, t).then((res: ApiResponse<any>) => {
                  if (res?.ok) {
                    setInterfaceTweaked(false);
                  }
                  return res;
                });
              }}
              disabled={!formikProps.dirty}
              state={applyButtonState}
            >
              {t('general.apply')}
            </Button>
          </div>
        )}
      </DynamicContainer>
      <FormSection title={t('audioEncoder.settings.settings')}>
        <DynamicContainer minColumns={2} id="dynamic-container-edit-audio-encoder">
          <FormSelect name="algorithm" title={t('audioEncoder.settings.algorithm')} options={algorithmOptions} />

          <FormSelect
            name="inputInterface"
            title={t('audioEncoder.settings.inputInterface')}
            options={inputInterfaceOptions}
            onChange={handleChangeInputInterface}
          />

          <div key="audenc-two-fields" className="mk-form-custom-two-checkbox-fields">
            <FormSelect
              name="mode"
              title={t('audioEncoder.settings.mode')}
              options={modeOptions}
              onChange={handleChangeMode}
            />
            <FormSelect
              className={values.interface === AudioInterfaceEnum.ANALOG && 'displayNone'}
              name="interface"
              title={t('audioEncoder.settings.channelLayout')}
              options={channelLayoutOptions}
              disabled={values.interface === AudioInterfaceEnum.ANALOG}
              onChange={() => {
                setInterfaceTweaked(false); // reset when manually selected
              }}
            />
          </div>

          {formikProps.values.algorithm !== AudioAlgorithmEnum.RAW && (
            <FormTypeahead
              name="bitRate"
              className="mk-typeahead-no-x"
              alwaysOpenWhenClicked={true}
              hideSearchIcon={true}
              showAllItems={true}
              title={t('audioEncoder.settings.bitRate')}
              placeholder={t('audioEncoder.settings.bitRatePlaceholder', {
                min: getBitRateRange().min,
                max: getBitRateRange().max,
              })}
              onBlur={(e) => {
                const bitrate = (e.target as any)?.value;
                const cleanedBitrateString = bitrate?.replace(/[^\d]+/g, '');
                if (bitrate === '') {
                  formikProps.setFieldTouched('bitRate');
                  formikProps.setFieldValue('bitRate', '');
                } else if (Number(bitrate) == bitrate) {
                  handleSelectBitrate(bitrate.toString());
                } else if (cleanedBitrateString > 0) {
                  const tentativeBitrate = Number(cleanedBitrateString);
                  if ((tentativeBitrate != formikProps.values.bitRate || bitrate?.length !== cleanedBitrateString?.length) && cleanedBitrateString == tentativeBitrate) {
                    handleSelectBitrate(cleanedBitrateString);
                  }
                }
              }}
              onEnterKey={(e) => {
                const bitrate = (e.target as any)?.value;
                const cleanedBitrateString = bitrate?.replace(/kbps/, '');
                if (Number(bitrate) == bitrate) {
                  handleSelectBitrate(bitrate.toString());
                } else if (cleanedBitrateString > 0) {
                  const tentativeBitrate = Number(cleanedBitrateString);
                  if (tentativeBitrate != formikProps.values.bitRate && cleanedBitrateString == tentativeBitrate) {
                    handleSelectBitrate(cleanedBitrateString);
                  }
                } else if (bitrate === '') {
                  formikProps.setFieldTouched('bitRate');
                  formikProps.setFieldValue('bitRate', 0);
                }
              }}
              listItems={bitrateOptions.map((l) => ({
                eventKey: l.value.toString(),
                title: l.label,
              }))}
              onSelect={handleSelectBitrate}
              selectedValue={printedBitRateValue} // FIXMWE still doesnt react to it when going for example from '128 kbps' to '128 kbps' when garbage is entered and cleaned up back to kbps
            />
          )}

          {formikProps.values.algorithm !== AudioAlgorithmEnum.RAW && (
            <FormSelect
              name="sampleRate"
              title={t('audioEncoder.settings.sampleRate')}
              options={sampleRateOptions}
              onChange={handleChangeSampleRate}
              disableSingleOption={false}
            />
          )}

          <FormTypeahead
            name="lang"
            alwaysOpenWhenClicked={true}
            showAllItems={true}
            hideSearchIcon={true}
            title={t('audioEncoder.settings.lang')}
            selectedValue={getLanguageTxt(values.lang)}
            listItems={langOptions.map(
              (l) =>
                ({
                  eventKey: l.value,
                  title: l.label,
                } as ITypeaheadListItemsProps),
            )}
            //onChange={handleChangeLanguage}
            onSelect={handleSelectLanguage}
            onBlur={onLanguageEdit}
            onEnterKey={onLanguageEdit}
            onChange={handleOnChangeLanguage}
          />
          {inputLevelAnalog && (
            <FormSelect name="inputLevel" title={t('audioEncoder.settings.inputLevel')} options={inputLevelOptions} />
          )}
        </DynamicContainer>
      </FormSection>
    </>
  );
};
